import { useCallback, useRef, useState, useEffect, useReducer, useMemo, useLayoutEffect } from 'react';
import {
  NAV_HEIGHT,
  DEFAULT_DISCLOSURE_BANNER_HEIGHT,
  CLOSED_CARD_HEIGHT,
  DEFAULT_MAX_MOBILE_CARD_HEIGHT,
} from './constants';
import { useRAFScroll } from './useRAFScroll';

/**
 * This hook is used to:
 * set the height of each card based on viewport height, nave height, and disclosure banner height.
 * manage the open/closed state of each card as you scroll or jump down the page via the UI and RAF for smooth animations
 * magage the top offsets for each card to be sticky in the correct position as the next card hits its top boundary
 */

interface MeasurementState {
  viewportHeight: number;
  disclosureBannerHeight: number;
  cardsscrollTopDistance: number;
  maxMobileCardHeight: number | null;
}

type MeasurementAction =
  | { type: 'SET_VIEWPORT_HEIGHT'; payload: number }
  | { type: 'SET_DISCLOSURE_BANNER_HEIGHT'; payload: number }
  | { type: 'SET_CARDSSCROLL_TOP_DISTANCE'; payload: number }
  | { type: 'SET_MAX_MOBILE_CARD_HEIGHT'; payload: number | null };

const initialMeasurementState: MeasurementState = {
  viewportHeight: 0,
  disclosureBannerHeight: DEFAULT_DISCLOSURE_BANNER_HEIGHT,
  cardsscrollTopDistance: 0,
  maxMobileCardHeight: null,
};

const measurementReducer = (state: MeasurementState, action: MeasurementAction): MeasurementState => {
  switch (action.type) {
    case 'SET_VIEWPORT_HEIGHT':
      return { ...state, viewportHeight: action.payload };
    case 'SET_DISCLOSURE_BANNER_HEIGHT':
      return { ...state, disclosureBannerHeight: action.payload };
    case 'SET_CARDSSCROLL_TOP_DISTANCE':
      return { ...state, cardsscrollTopDistance: action.payload };
    case 'SET_MAX_MOBILE_CARD_HEIGHT':
      return { ...state, maxMobileCardHeight: action.payload };
    default:
      return state;
  }
};

const getInitialCardTops = (cardCount: number): number[] => {
  if (cardCount === 0) return [];

  const perCardHeight = DEFAULT_MAX_MOBILE_CARD_HEIGHT;
  const SNAP_TOP_POS = 0;
  const SNAP_BOTTOM_POS = Math.max(perCardHeight - CLOSED_CARD_HEIGHT * 2, 0);
  const SNAP_BOTTOM_POS_2 = Math.max(perCardHeight - CLOSED_CARD_HEIGHT, 0);
  const SNAP_BELOW_POS = perCardHeight;

  return Array.from({ length: cardCount }, (_, index) => {
    if (index === 0) return SNAP_TOP_POS;
    if (index === 1) {
      const isSecondToLastActive = cardCount === 2;
      return isSecondToLastActive ? SNAP_BOTTOM_POS_2 : SNAP_BOTTOM_POS;
    }
    if (index === 2) return SNAP_BOTTOM_POS_2;
    return SNAP_BELOW_POS;
  });
};

export const useVerticalScroll = (cardCount: number) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewportElementRef = useRef<HTMLDivElement>(null);
  const previousBannerExistsRef = useRef<boolean>(false);
  const [measurements, dispatch] = useReducer(measurementReducer, initialMeasurementState);

  const [cardTops, setCardTops] = useState<number[]>(() => getInitialCardTops(cardCount));

  const [expandedStates, setExpandedStates] = useState<boolean[]>(() => {
    if (cardCount === 0) return [];
    return [true, ...new Array(cardCount - 1).fill(false)];
  });

  // ------------------------------------------------------------
  //MARK: Measurement funcs

  // Get viewport height and save it to state
  const getViewportHeight = useCallback(() => {
    const height = window.innerHeight || document.documentElement.clientHeight;
    dispatch({ type: 'SET_VIEWPORT_HEIGHT', payload: height });
    return height;
  }, []);

  // Get the height of data-disclosure-banner="true" if present, falling back to 0
  const getDisclosureBannerHeight = useCallback(() => {
    const bannerElement = document.querySelector('[data-disclosure-banner="true"]');
    if (bannerElement instanceof HTMLElement) {
      const height = bannerElement.offsetHeight;
      dispatch({ type: 'SET_DISCLOSURE_BANNER_HEIGHT', payload: height });
      return height;
    }
    dispatch({ type: 'SET_DISCLOSURE_BANNER_HEIGHT', payload: 0 });
    return 0;
  }, []);

  // Get the distance of the container from top of page
  const getCardsscrollTopDistance = useCallback(() => {
    const cardsscrollElement = containerRef.current;
    if (cardsscrollElement) {
      const rect = cardsscrollElement.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const distance = rect.top + scrollTop;
      dispatch({ type: 'SET_CARDSSCROLL_TOP_DISTANCE', payload: distance });
      return distance;
    }
    dispatch({ type: 'SET_CARDSSCROLL_TOP_DISTANCE', payload: 0 });
    return 0;
  }, []);

  // Extract measurement values for use in callbacks (avoids infinite loops)
  const viewportHeight = useMemo(() => measurements.viewportHeight, [measurements.viewportHeight]);
  const disclosureBannerHeight = useMemo(
    () => measurements.disclosureBannerHeight,
    [measurements.disclosureBannerHeight],
  );
  const cardsscrollTopDistance = useMemo(
    () => measurements.cardsscrollTopDistance,
    [measurements.cardsscrollTopDistance],
  );
  const perCardHeight = useMemo(
    () => measurements.maxMobileCardHeight ?? DEFAULT_MAX_MOBILE_CARD_HEIGHT,
    [measurements.maxMobileCardHeight],
  );
  const trackHeight = useMemo(() => perCardHeight * Math.max(cardCount, 1), [perCardHeight, cardCount]);
  const stickyOffset = NAV_HEIGHT;

  // Calculate maxMobileCardHeight derived value
  const calculateMaxMobileCardHeight = useCallback(() => {
    const maxMobileCardHeight = Math.max(
      viewportHeight - NAV_HEIGHT - disclosureBannerHeight,
      DEFAULT_MAX_MOBILE_CARD_HEIGHT, // it's unusable below this, so we need a reasonable failsafe
    );
    dispatch({ type: 'SET_MAX_MOBILE_CARD_HEIGHT', payload: maxMobileCardHeight });
    return maxMobileCardHeight;
  }, [viewportHeight, disclosureBannerHeight]);

  // Measure all values
  const measureAll = useCallback(() => {
    getViewportHeight();
    getDisclosureBannerHeight();
    getCardsscrollTopDistance();
  }, [getViewportHeight, getDisclosureBannerHeight, getCardsscrollTopDistance]);

  // ------------------------------------------------------------
  //MARK: useEffects funcs

  // Calculate maxMobileCardHeight whenever dependencies change
  useEffect(() => {
    calculateMaxMobileCardHeight();
  }, [calculateMaxMobileCardHeight]);

  useLayoutEffect(() => {
    measureAll();
  }, [measureAll]);

  // Measure on window resize
  // Only measure when in mobile view
  useEffect(() => {
    window.addEventListener('resize', measureAll);
    return () => window.removeEventListener('resize', measureAll);
  }, [measureAll]);

  // ------------------------------------------------------------
  // MARK: Disclosure observer

  // Monitor data-disclosure-banner presence in DOM
  // Recalculate measurements if it's added or removed
  useEffect(() => {
    const checkBannerPresence = () => {
      const bannerElement = document.querySelector('[data-disclosure-banner="true"]');
      const bannerExists = bannerElement !== null;

      // Only trigger if the presence changed
      if (bannerExists !== previousBannerExistsRef.current) {
        previousBannerExistsRef.current = bannerExists;
        measureAll();
        calculateMaxMobileCardHeight();
      }
    };

    // Initial check
    checkBannerPresence();

    // Set up MutationObserver to watch for DOM changes
    const observer = new MutationObserver(() => {
      checkBannerPresence();
    });

    // Observe the entire document for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-disclosure-banner'],
    });

    return () => {
      observer.disconnect();
    };
  }, [measureAll, calculateMaxMobileCardHeight]);

  // ------------------------------------------------------------
  // MARK: RAF for scroll snapping logic
  const updateCardTops = useCallback(() => {
    if (cardCount === 0) {
      setCardTops([]);
      setExpandedStates([]);
      return;
    }

    const scrollableHeight = Math.max(trackHeight - perCardHeight, 1);

    const currentScrollPos = window.pageYOffset || document.documentElement.scrollTop;

    const relativeScroll = currentScrollPos - cardsscrollTopDistance;
    const progress = Math.max(0, Math.min(1, relativeScroll / scrollableHeight));

    let activeIndex = Math.floor(progress * cardCount);
    activeIndex = Math.min(activeIndex, cardCount - 1);

    const SNAP_TOP_POS = 0;
    const SNAP_ABOVE_POS = -NAV_HEIGHT;
    const SNAP_BOTTOM_POS = Math.max(perCardHeight - CLOSED_CARD_HEIGHT * 2, 0);
    const SNAP_BOTTOM_POS_2 = Math.max(perCardHeight - CLOSED_CARD_HEIGHT, 0); // Second closed card
    const SNAP_BELOW_POS = perCardHeight;

    const newExpandedStates = new Array<boolean>(cardCount);
    const newCardTops = new Array<number>(cardCount);

    for (let i = 0; i < cardCount; i++) {
      const isExpanded = i === activeIndex;
      newExpandedStates[i] = isExpanded;

      if (i < activeIndex) {
        newCardTops[i] = SNAP_ABOVE_POS;
      } else if (i === activeIndex) {
        newCardTops[i] = SNAP_TOP_POS;
      } else if (i === activeIndex + 1) {
        // Special case: when second-to-last card is active, position the last card at SNAP_BOTTOM_POS_2
        const isSecondToLastActive = activeIndex === cardCount - 2;
        newCardTops[i] = isSecondToLastActive ? SNAP_BOTTOM_POS_2 : SNAP_BOTTOM_POS;
      } else if (i === activeIndex + 2) {
        newCardTops[i] = SNAP_BOTTOM_POS_2;
      } else {
        newCardTops[i] = SNAP_BELOW_POS;
      }
    }
    setCardTops(newCardTops);
    setExpandedStates(newExpandedStates);
  }, [cardCount, perCardHeight, trackHeight, cardsscrollTopDistance]);

  useRAFScroll({
    onScroll: updateCardTops,
    enabled: measurements.maxMobileCardHeight !== null && cardCount > 0,
  });

  // Scroll the card to just below the nav
  const handleNavScroll = useCallback(
    (index: number) => {
      if (cardCount <= 1) {
        window.scrollTo({ top: cardsscrollTopDistance, behavior: 'smooth' });
        return;
      }

      // Find currently expanded card index
      const currentExpandedIndex = expandedStates.findIndex(isExpanded => isExpanded);

      // If clicking the already expanded last card, scroll past the component
      if (index === cardCount - 1 && index === currentExpandedIndex) {
        const scrollPastPos = cardsscrollTopDistance + trackHeight - NAV_HEIGHT;
        window.scrollTo({ top: scrollPastPos, behavior: 'smooth' });
        return;
      }

      // If clicking the already expanded card (not last), advance to next
      // Otherwise (clicking a collapsed card), scroll to that card
      const targetIndex = index === currentExpandedIndex ? Math.min(index + 1, cardCount - 1) : index;

      const scrollableHeight = Math.max(trackHeight - perCardHeight, 0);
      const targetProgress = Math.min(targetIndex / (cardCount - 1), 1);
      let targetScroll = cardsscrollTopDistance + targetProgress * scrollableHeight;

      // Fix for last card alignment:
      // Ensure we don't scroll past the sticky boundary by accounting for NAV_HEIGHT
      // This keeps the card aligned under the nav instead of being pushed up to the top of the viewport
      if (targetIndex === cardCount - 1) {
        targetScroll -= NAV_HEIGHT;
      }

      window.scrollTo({
        top: targetScroll,
        behavior: 'smooth',
      });
    },
    [cardsscrollTopDistance, expandedStates, trackHeight, perCardHeight, cardCount],
  );

  return {
    containerRef,
    viewportRef: viewportElementRef,
    maxMobileCardHeight: measurements.maxMobileCardHeight ?? DEFAULT_MAX_MOBILE_CARD_HEIGHT,
    cardTops,
    expandedStates,
    handleNavScroll,
    trackHeight,
    stickyOffset,
  };
};
